
import {
  Component, Prop, Watch,
} from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import InputFieldMixin from '~/components/Inputs/InputFieldMixin';
import HiddenInput from '@/components/common/Inputs/HiddenInput.vue';
import Debounce from '@/decorators/debounce';
import {
  containsLowercaseCharacters,
  containsUppercaseCharacters,
  containsNumbers,
  hasMinEightCharacters,
  isValidPassword
} from '@/utils/validators';

@Component({ components: { HiddenInput } })
export default class Password extends mixins(InputFieldMixin) {
  @Prop({ default: '' }) readonly value!: string;

  @Prop(Boolean) readonly showIcon!: boolean;

  @Prop(Boolean) readonly showRequirementsTooltip!: boolean;

  @Prop(Boolean) readonly skipValidation!: boolean;

  @Prop({ default: 'top' }) private readonly tooltipPosition!: string;

  @Prop({ default: '' }) readonly passwordVerify!: string;

  passwordErrorMessages: string[] = [];

  defaultDataTestAttr = 'passwordInput';

  defaultLabelAttr = 'Password';

  isFocused = false;

  get tooltipClass(): string {
    return `password-tooltip--${this.tooltipPosition}`;
  }

  @Debounce()
  async passwordValidation(value: string) {
    if (this.skipValidation) return true;

    this.passwordErrorMessages = [];

    if (!value) return this.isRequired();

    if (!isValidPassword(value)) this.passwordErrorMessages = ['Please correct password format'];

    const { passwordVerify } = this;

    if (passwordVerify && this.requirementsMet && passwordVerify !== value) {
      this.passwordErrorMessages = ['Passwords do not match'];
    }

    return true;
  }

  onBlur(e: never) {
    this.isFocused = false;
    this.$emit('blur', e);
  }

  get passwordRequirements(): Array<{ label: string; isValid: boolean }> {
    return [
      { label: 'Minimum 8 Characters', isValid: this.passwordHasMinEightCharacters },
      { label: 'At Least One Lowercase Letter', isValid: this.passwordHasLowerCaseCharacters },
      { label: 'At Least One Uppercase Letter', isValid: this.passwordHasUppercaseCharacters },
      { label: 'At Least One Number', isValid: this.passwordHasNumbers },
    ];
  }

  get passwordHasLowerCaseCharacters(): boolean {
    return containsLowercaseCharacters(this.value);
  }

  get passwordHasNumbers(): boolean {
    return containsNumbers(this.value);
  }

  get passwordHasUppercaseCharacters(): boolean {
    return containsUppercaseCharacters(this.value);
  }

  get passwordHasMinEightCharacters(): boolean {
    return hasMinEightCharacters(this.value);
  }

  get requirementsMet() {
    return this.passwordRequirements.every(item => {
      return item.isValid === true;
    });
  }

  @Watch('value')
  private handleValuePasswordChange() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (this.passwordVerify) (this.$refs.input as any).validate();

    if (this.value === this.passwordVerify && this.requirementsMet) {
      this.passwordErrorMessages = [];
    }
  }

  @Watch('passwordVerify')
  private handleVerifyPasswordChange() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (this.value) (this.$refs.input as any).validate();

    if (this.passwordVerify === this.value && this.requirementsMet) {
      this.passwordErrorMessages = [];
    }
  }
}
